import { Controller } from '@hotwired/stimulus';

/* global Twine */

// Replace, append, or remove controller when a browser event
// containing stringified html template(s) in its `detail` payload is heard.
// Supports optional `templates`, allowing the event to update multiple controllers.
//
// Event payload ex:
// new CustomEvent('kjb:event:name', {
//   detail: {
//      content:  '<div>html string</div>',
//      templates: { navigation: '<div>html string</div>' }  <-- (optional)
//   }
// })
//
// Html ex:
// <div
//   data-controller="remote"
//   action="kjb:event:name@document->remote#replaceInner"
//   remote-template-value="navigation" <-- (optional)
// >
//   ...
// </div>

export default class extends Controller {
  static values = { template: String };

  replaceInner(event) {
    this._processResponse(event, (fragment) => {
      const container = this.element;

      if (container && fragment) {
        this._removeChildren(container);
        container.appendChild(fragment);
        this._refreshContainer(container);
      }
    });
  }

  replace(event) {
    this._processResponse(event, (fragment) => {
      const container = this.element;

      if (container && fragment) {
        container.replaceWith(fragment);
      }
    });
  }

  append(event) {
    this._processResponse(event, (fragment) => {
      const container = this.element;

      if (container && fragment) {
        container.append(fragment);
        this._refreshContainer(container);
      }
    });
  }

  remove(event) {
    this._processResponse(event, () => {
      const container = this.element;

      if (container) {
        container.remove();
        this._refreshContainer(container);
      }
    });
  }

  removeInner(event) {
    this._processResponse(event, () => {
      const container = this.element;

      if (container) {
        this._removeChildren(container);
        this._refreshContainer(container);
      }
    });
  }

  _processResponse({ detail = {} }, callback) {
    let htmlString;

    if (this.hasTemplateValue) {
      htmlString = detail.templates && detail.templates[this.templateValue];
    } else {
      htmlString = detail.content;
    }

    const fragment = htmlString && document.createRange().createContextualFragment(htmlString);
    callback(fragment);
  }

  _removeChildren(container) {
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
  }

  _refreshContainer(container) {
    if (typeof Twine !== 'undefined') {
      Twine.bind(container);
      Twine.refresh();
    }
  }
}
